/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { login, loginVerify, resendOtp } from '../core/_requests'
import { useAuth } from '../core/Auth'
import Swal from 'sweetalert2'

const loginSchema = Yup.object().shape({
  identifier: Yup.string()
    .required('Email or phone is required')
    .test('identifier-format', 'Enter a valid email or phone number', (value) => {
      if (!value) {
        return false
      }

      const trimmed = value.trim()
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      const isPhone = /^\+?[0-9]{7,15}$/.test(trimmed)
      return isEmail || isPhone
    }),
})

const initialValues = {
  identifier: '',
}

const initialValuesOTP: any = {
  otp: {
    a: '',
    b: '',
    c: '',
    d: '',
    e: '',
    f: '',
  },
}

export function Login() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  const [otpResendMessage, setOtpResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const getApiErrorMessage = (error: any, fallback: string) => {
    return (
      error?.response?.data?.data?.detail ||
      error?.response?.data?.data?.message ||
      error?.response?.data?.message ||
      fallback
    )
  }


  const handleResendOtp = async () => {
    setResendDisabled(true)
    setResendTimer(120)
    setOtpResendMessage(null)
    try {
      await resendOtp(formik.values.identifier)
      setOtpResendMessage({ type: 'success', text: intl.formatMessage({id: 'AUTH.LOGIN.RESEND_SUCCESS'}) })
    } catch (error: any) {
      console.error(error)
      setOtpResendMessage({
        type: 'error',
        text: getApiErrorMessage(error, intl.formatMessage({id: 'AUTH.LOGIN.RESEND_FAILED'})),
      })
    }
  }
  useEffect(() => {
    if (otpResendMessage) {
      const timeout = setTimeout(() => setOtpResendMessage(null), 4000)
      return () => clearTimeout(timeout)
    }
  }, [otpResendMessage])
  useEffect(() => {
    let timerInterval: any

    if (resendTimer > 0) {
      timerInterval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev === 1) {
            setResendDisabled(false)
            clearInterval(timerInterval)
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(timerInterval)
  }, [resendTimer])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.identifier)
        Swal.fire({
          title: auth?.message || intl.formatMessage({id: 'AUTH.LOGIN.OTP_SENT_TITLE'}),
          text: auth?.data?.detail || intl.formatMessage({id: 'AUTH.LOGIN.OTP_SENT_DETAIL'}),
          icon: 'success',

        })
        setPhone(auth?.data?.phone || '')
        setEmail(auth?.data?.email || '')
        setOtpSent(true)
        setLoading(false)
      } 
      // catch (error) {
      //   console.error(error)
      //   saveAuth(undefined)
      //   setStatus('The login details are incorrect')
      //   setSubmitting(false)
      //   setLoading(false)
      // }

       catch (error) {
        console.error(error)
        // remove saveAuth(undefined) — no auth exists yet to clear
        setStatus(getApiErrorMessage(error, 'The login details are incorrect'))
        setSubmitting(false)
        setLoading(false)
}
    },
  })

  const formikOTP = useFormik({
    initialValues: initialValuesOTP,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const mOTP =
          values.otp.a + values.otp.b + values.otp.c + values.otp.d + values.otp.e + values.otp.f
        if (!mOTP || mOTP.length !== 6) {
          setStatus('Please enter correct 6 digit otp !!!')
          setSubmitting(false)
          setLoading(false)
          return;
        }
        const resp = await loginVerify(formik.values.identifier, mOTP)
        const authData = resp.data.data
        saveAuth(authData)
        setCurrentUser(authData?.user)
      } catch (error: any) {
        // saveAuth(undefined)
        setStatus(getApiErrorMessage(error, 'Internal Server Error ! Please try again later.'))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const onKeyUpFun = (e: any, prevData, nextData) => {
    if (e.keyCode === 8 || e.keyCode === 37) {
      const prev = document.getElementById('otpInput' + prevData) as HTMLInputElement
      if (prev) {
        prev.select()
      }
    } else if (
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      (e.keyCode >= 65 && e.keyCode <= 90) ||
      (e.keyCode >= 96 && e.keyCode <= 105) ||
      e.keyCode === 39
    ) {
      const next = document.getElementById('otpInput' + nextData) as HTMLInputElement
      if (next) {
        next.select()
      }
    }
  }
  const handlePaste = (ev: any) => {
    const clip = ev.clipboardData.getData('text')
    if (!isNaN(clip) && Number.isInteger(parseInt(clip)) && clip.length >= 6) {
      const tempOTP = {
        a: clip[0],
        b: clip[1],
        c: clip[2],
        d: clip[3],
        e: clip[4],
        f: clip[5],
      }
      formikOTP.setValues((prev) => ({ ...prev, otp: tempOTP }))
      const mInput = document.getElementById('otpInput6') as HTMLInputElement
      mInput.select()
    }
  }

  return otpSent ? (
    <form
      className='form w-100'
      onSubmit={formikOTP.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        <img src='/media/logos/goflipo_color_new.png' className='mh-90px' alt='GoFlipo logo' />
        {/* <img src='/media/logos/transparency-dark.png' className='w-100' /> */}
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.LOGIN.GOFLIPO_ADMIN'})}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>{intl.formatMessage({id: 'AUTH.LOGIN.SUBTITLE'})}</div>
      </div>

      {formikOTP.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formikOTP.status}</div>
        </div>
      ) : null}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        {phone && (
          <div className="mb-4">
            <p >
              {intl.formatMessage({id: 'AUTH.LOGIN.OTP_SENT_TO'})} <strong className='masked-phone'>{phone}</strong>{email ? ` and ${email}` : ''}
            </p>

          </div>
        )}
        <label className='form-label fs-6 fw-bolder text-dark'>
          {intl.formatMessage({id: 'AUTH.LOGIN.OTP_TITLE'})}
        </label>
        <div className='d-flex gap-2 mt-2'>
          <input
            className={'form-control bg-transparent h-60px fs-2x text-center'}
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.a}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, a: e.target.value } }))
            }}
            id='otpInput1'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, '', 2)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.b}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, b: e.target.value } }))
            }}
            id='otpInput2'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 1, 3)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.c}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, c: e.target.value } }))
            }}
            id='otpInput3'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 2, 4)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.d}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, d: e.target.value } }))
            }}
            id='otpInput4'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 3, 5)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.e}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, e: e.target.value } }))
            }}
            id='otpInput5'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 4, 6)}
          />
          <input
            className='form-control bg-transparent h-60px fs-2x text-center'
            type='text'
            autoComplete='off'
            maxLength={1}
            value={formikOTP.values.otp.f}
            onChange={(e) => {
              formikOTP.setValues((prev) => ({ ...prev, otp: { ...prev.otp, f: e.target.value } }))
            }}
            id='otpInput6'
            onPaste={handlePaste}
            onKeyUp={(e) => onKeyUpFun(e, 5, '')}
          />
        </div>
      </div>
      {/* end::Form group */}
      {otpResendMessage && (
        <div className={`alert alert-${otpResendMessage.type === 'success' ? 'success' : 'danger'} mt-2`}>
          <div className='alert-text'>{otpResendMessage.text}</div>
        </div>
      )}
      <div className='d-grid mb-8 justify-content-end'>
        <button
          type='button'
          className='btn btn-link p-0'
          disabled={loading || resendDisabled}
          onClick={handleResendOtp}
        >
          {(
            <span className='indicator-label'>
              {resendDisabled
                ? intl.formatMessage({id: 'AUTH.LOGIN.RESEND_OTP_TIMER'}, {seconds: resendTimer})
                : intl.formatMessage({id: 'AUTH.LOGIN.RESEND_OTP'})}
            </span>
          )}

        </button>
      </div>
      <p className="text-warning text-center">
        {intl.formatMessage({id: 'AUTH.LOGIN.MAX_RETRIES'})}
      </p>
      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formikOTP.isSubmitting || !formikOTP.isValid}
        >
          {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.LOGIN.SUBMIT_OTP'})}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      {/* <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <Link to='#' className='link-primary'>
          Contact Us
        </Link>
      </div> */}
    </form>
  ) : (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        <img src='/media/logos/goflipo_color_new.png' className='mh-90px theme-light-show' alt='GoFlipo logo' />
        <img src='/media/logos/goflipo_white_new.png' className='mh-90px theme-dark-show' alt='GoFlipo logo' />
        {/* <img src='/media/logos/transparency-dark.png' className='w-100' /> */}
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.LOGIN.GOFLIPO_ADMIN'})}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>{intl.formatMessage({id: 'AUTH.LOGIN.SUBTITLE'})}</div>
      </div>

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : null}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({id: 'AUTH.LOGIN.EMAIL_LABEL'})}</label>
        <input
          placeholder={intl.formatMessage({id: 'AUTH.LOGIN.EMAIL_PLACEHOLDER'})}
          {...formik.getFieldProps('identifier')}
          className={clsx(
            'form-control bg-transparent'
          )}
          type='text'
          name='identifier'
          autoComplete='off'
        />
        {formik.touched.identifier && formik.errors.identifier && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.identifier}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.LOGIN.GET_OTP'})}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      {/* <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <Link to='#' className='link-primary'>
          Contact Us
        </Link>
      </div> */}
    </form>
  )
}
